/**
 * `/settings/webhooks` — list + management entry point.
 *
 * Replaces the pre-phase-3 placeholder (`webhooks.tsx`). Uses the
 * real `useEndpoints()` hook from `@/lib/queries/webhooks`, backed
 * by `GET /v1/webhooks/endpoints` (phase 2).
 *
 * Routing:
 *   - Admins see "Create endpoint" → opens create slide-over at
 *     `/settings/webhooks/new`.
 *   - Row-click → opens edit slide-over at
 *     `/settings/webhooks/$uuid/edit`.
 *   - "View deliveries" button → `/settings/webhooks/$uuid/deliveries`
 *     (full-page deliveries log).
 *   - "Rotate secret" runs inline (no slide-over) — the reveal
 *     modal handles the one-time secret display.
 *   - "Delete" opens a ConfirmDialog.
 *
 * Status badge mapping:
 *   - `status: 'enabled'` + `auto_disabled_at: null`  → "enabled" (success tone)
 *   - `status: 'disabled'` + `auto_disabled_at: null` → "disabled" (muted)
 *   - `auto_disabled_at != null`                      → "auto-disabled" (danger) —
 *       the server trips this after consecutive failures exceed the threshold;
 *       manual re-enable via `PATCH { status: 'enabled' }` clears it.
 *
 * Admin-gate is cosmetic — the server enforces `requireAdminLike` on
 * all /v1/webhooks/* routes.
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { ErrorState } from '@/components/primitives/ErrorState';
import { PageActions } from '@/components/primitives/PageActions';
import { SigningSecretReveal } from '@/components/primitives/SigningSecretReveal';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toastFromError, toastSuccess } from '@/lib/errors';
import { relativeTime } from '@/lib/format';
import type { RotateSecretResponse, WebhookEndpoint } from '@/lib/queries';
import { useDeleteEndpoint, useEndpoints, useRotateSecret, useWorkspace } from '@/lib/queries';
import { useAuth } from '@clerk/react';
import {
  ArrowsClockwise,
  ListBullets,
  PencilSimple,
  Plus,
  Trash,
  WebhooksLogo,
} from '@phosphor-icons/react';
import { Link, Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/settings/webhooks/')({
  component: WebhooksIndex,
});

function WebhooksIndex(): ReactElement {
  const { orgId } = useAuth();
  const workspace = useWorkspace();
  const endpoints = useEndpoints();
  const rotate = useRotateSecret();
  const del = useDeleteEndpoint();
  const navigate = useNavigate();

  const [toRotate, setToRotate] = useState<WebhookEndpoint | null>(null);
  const [rotating, setRotating] = useState(false);
  const [rotated, setRotated] = useState<{
    endpoint: WebhookEndpoint;
    result: RotateSecretResponse;
  } | null>(null);
  const [toDelete, setToDelete] = useState<WebhookEndpoint | null>(null);
  const [deleting, setDeleting] = useState(false);

  const activeOrg = workspace.data?.organisations.find((o) => o.auth_provider_id === orgId);
  const isAdmin = activeOrg?.role === 'admin';

  const rows = endpoints.data?.endpoints ?? [];

  async function onRotate(): Promise<void> {
    if (!toRotate) return;
    setRotating(true);
    try {
      const result = await rotate.mutateAsync({ uuid: toRotate.uuid });
      setRotated({ endpoint: toRotate, result });
      setToRotate(null);
    } catch (err) {
      toastFromError(err);
    } finally {
      setRotating(false);
    }
  }

  async function onDelete(): Promise<void> {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await del.mutateAsync({ uuid: toDelete.uuid });
      toastSuccess('Endpoint deleted', `${toDelete.url} will no longer receive events.`);
      setToDelete(null);
    } catch (err) {
      toastFromError(err);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Webhooks</h2>
          <p className="text-sm text-[var(--color-mute)]">
            Configure HTTPS endpoints, Slack incoming webhooks, or Discord webhooks to receive event
            notifications. HMAC signing is opt-in per endpoint — leave it off for Slack and Discord
            (their servers don't verify).
          </p>
        </div>
        {isAdmin && (
          <PageActions>
            <Button render={<Link to="/settings/webhooks/new" />}>
              <Plus aria-hidden="true" />
              Create endpoint
            </Button>
          </PageActions>
        )}
      </header>

      {endpoints.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      )}

      {endpoints.isError && !endpoints.isLoading && (
        <ErrorState
          title="We couldn't load webhook endpoints"
          error={endpoints.error}
          retry={() => {
            void endpoints.refetch();
          }}
        />
      )}

      {!endpoints.isLoading && !endpoints.isError && rows.length === 0 && (
        <EmptyState
          icon={WebhooksLogo}
          title="No endpoints yet"
          description="You haven't configured any webhooks yet. Create one to get notified when audits complete or fail."
          action={
            isAdmin ? (
              <Button render={<Link to="/settings/webhooks/new" />}>
                <Plus aria-hidden="true" />
                Create endpoint
              </Button>
            ) : undefined
          }
        />
      )}

      {!endpoints.isLoading && !endpoints.isError && rows.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line-soft)] text-left text-xs uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium">URL</th>
                  <th className="px-4 py-2 font-medium">Kind</th>
                  <th className="px-4 py-2 font-medium">Signing</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Events</th>
                  <th className="px-4 py-2 font-medium">Failures</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.uuid} className="border-b border-[var(--color-line-soft)]/70">
                    <td className="px-4 py-3 text-sm font-medium text-[var(--color-ink)]">
                      {row.description ?? <span className="text-[var(--color-mute)]">—</span>}
                    </td>
                    <td className="max-w-xs px-4 py-3 font-mono text-xs text-[var(--color-ink-soft)]">
                      <span className="block truncate" title={row.url}>
                        {row.url}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--color-ink-soft)]">{row.kind}</td>
                    <td className="px-4 py-3">
                      <SigningBadge enabled={row.signing_enabled} />
                    </td>
                    <td className="px-4 py-3">
                      <EndpointStatusBadge endpoint={row} />
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--color-mute)]">
                      {row.event_types.length === 0 ? 'all' : row.event_types.join(', ')}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--color-mute)]">
                      {row.consecutive_failures}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--color-mute)]">
                      {relativeTime(row.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Deliveries for ${row.url}`}
                          onClick={() =>
                            navigate({
                              to: '/settings/webhooks/$uuid/deliveries',
                              params: { uuid: row.uuid },
                            })
                          }
                        >
                          <ListBullets aria-hidden="true" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Edit ${row.url}`}
                          onClick={() =>
                            navigate({
                              to: '/settings/webhooks/$uuid/edit',
                              params: { uuid: row.uuid },
                            })
                          }
                        >
                          <PencilSimple aria-hidden="true" />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`Rotate secret for ${row.url}`}
                              onClick={() => setToRotate(row)}
                            >
                              <ArrowsClockwise aria-hidden="true" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`Delete ${row.url}`}
                              onClick={() => setToDelete(row)}
                            >
                              <Trash aria-hidden="true" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Slide-over children render here — `new`, `$uuid/edit`, `$uuid/deliveries`
          are all nested under this index route. */}
      <Outlet />

      <ConfirmDialog
        open={toRotate !== null}
        onOpenChange={(next) => {
          if (!next) setToRotate(null);
        }}
        title="Rotate signing secret?"
        description={
          toRotate
            ? `A new secret for ${toRotate.url} will be generated. The current secret will keep verifying for 24 hours while you deploy the new one.`
            : undefined
        }
        confirmLabel="Rotate"
        loading={rotating}
        onConfirm={onRotate}
      />

      <SigningSecretReveal
        secret={rotated?.result.signing_secret ?? null}
        onClose={() => setRotated(null)}
        title="New signing secret"
        description="Copy the new secret and deploy it to your receiver. Both the new and previous secrets verify during the 24h grace window; after that, only this one is accepted. You can look it up again later from the endpoint detail page."
        codeLabel="New secret"
        meta={
          rotated && (
            <>
              The previous secret will keep verifying until{' '}
              <span className="font-mono">
                {new Date(rotated.result.previous_secret_expires_at).toLocaleString()}
              </span>
              . After that, only the new secret is accepted.
            </>
          )
        }
      />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(next) => {
          if (!next) setToDelete(null);
        }}
        title="Delete this endpoint?"
        description={
          toDelete
            ? `${toDelete.url} will stop receiving events immediately. The delivery history is kept for audit purposes.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={deleting}
        onConfirm={onDelete}
      />
    </div>
  );
}

function EndpointStatusBadge({ endpoint }: { endpoint: WebhookEndpoint }): ReactElement {
  if (endpoint.auto_disabled_at !== null) {
    return (
      <span className="inline-flex items-center rounded-full bg-[var(--color-danger)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-danger)]">
        auto-disabled
      </span>
    );
  }
  if (endpoint.status === 'enabled') {
    return (
      <span className="inline-flex items-center rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-success)]">
        enabled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--color-paper-alt)] px-2 py-0.5 text-xs font-medium text-[var(--color-ink-soft)]">
      disabled
    </span>
  );
}

/**
 * Signing toggle indicator. "Signed" when the consumer adds an
 * `X-Clickweave-Signature` header to deliveries, "Unsigned" when it
 * skips that header (the default for new endpoints, and the only
 * sensible state for Slack/Discord whose servers don't verify).
 */
function SigningBadge({ enabled }: { enabled: boolean }): ReactElement {
  if (enabled) {
    return (
      <span className="inline-flex items-center rounded-full bg-[var(--color-paper-alt)] px-2 py-0.5 text-xs font-medium text-[var(--color-ink)]">
        Signed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--color-paper-alt)] px-2 py-0.5 text-xs font-medium text-[var(--color-ink-soft)]">
      Unsigned
    </span>
  );
}
